import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import {
  ANY_SPORT_MY_CARDS_CARDS_TYPE,
  MLB_MARKET_COMMON_MARKET_DROP_CARD,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { getCommonCardShardsCount } from '@sorare/core/src/lib/currentUser';
import { glossary } from '@sorare/core/src/lib/glossary';

import { Content } from './Content/index.tsx';
import { Summary } from './Summary/index.tsx';
import type { CommonPaymentBoxProps } from './types.tsx';
import { CommonPaymentBoxMode } from './types.tsx';
import { useDropAndAddCards } from './useDropAndAddCards.ts';

export { CommonPaymentBoxMode } from './types.tsx';

const title = defineMessages({
  [CommonPaymentBoxMode.ADD]: {
    id: 'commonPaymentBox.title.buy',
    defaultMessage: 'Add a Card',
  },
  [CommonPaymentBoxMode.DROP]: {
    id: 'commonPaymentBox.title.sell',
    defaultMessage: 'Drop a Card',
  },
});

const submitMessage = defineMessages({
  [CommonPaymentBoxMode.ADD]: {
    id: 'commonPaymentBox.submit.buy',
    defaultMessage: 'Add',
  },
  [CommonPaymentBoxMode.DROP]: {
    id: 'commonPaymentBox.submit.sell',
    defaultMessage: 'Drop',
  },
});

const Root = styled(Vertical)`
  height: 100%;
  min-height: 450px;
`;
const Grow = styled(Vertical).attrs({ gap: 2 })`
  height: 100%;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  padding: 0 var(--double-unit);
`;

const Warning = styled(Horizontal)`
  width: 100%;
  padding: var(--unit);
  background: var(--c-surface-caution);
  border-radius: var(--unit);
  justify-content: space-between;
`;

const Title = styled(BodyM).attrs({ bold: true })``;

const Footer = styled(Horizontal)`
  width: 100%;
  padding: var(--double-unit);
`;
export const CommonPaymentBox = ({
  subtotal,
  fees,
  total,
  slug,
  signedPrice,
  mode,
  open,
  onClose,
  onSuccess,
  commonCardSummaryNode,
  attachCardsToUserCollections = false,
}: CommonPaymentBoxProps) => {
  const { generateSportContextPath, sport } = useSportContext();
  const { dropAndAddCards } = useDropAndAddCards();
  const buying = mode === CommonPaymentBoxMode.ADD;
  const { currentUser } = useCurrentUserContext();

  const insufficientBalance =
    buying && getCommonCardShardsCount(currentUser, 'common-base-set') < total;

  const submit = async () => {
    let mutationData;
    if (mode === CommonPaymentBoxMode.ADD) {
      mutationData = await dropAndAddCards([], [{ signedPrice }], {
        attachCardsToUserCollections,
      });
    } else {
      mutationData = await dropAndAddCards(
        [{ cardSlug: slug, signedPrice }],
        []
      );
    }
    if (!mutationData?.errors?.length) {
      onSuccess?.();
    }
  };
  return (
    <Dialog
      title={
        <Title className="text-center">
          <FormattedMessage {...title[mode]} />
        </Title>
      }
      open={open}
      fullWidth
      maxWidth="xs"
      onClose={onClose}
      footer={
        <Footer>
          <Button
            disabled={insufficientBalance}
            fullWidth
            onClick={submit}
            size="medium"
            color="primary"
          >
            <FormattedMessage {...submitMessage[mode]} />
          </Button>
        </Footer>
      }
    >
      <Root>
        {fees > 0 && <Summary subtotal={subtotal} fees={fees} />}
        <Grow>
          <Content
            total={total}
            mode={mode}
            balance={
              getCommonCardShardsCount(currentUser, 'common-base-set') || 0
            }
          />
          {!!insufficientBalance && (
            <Warning>
              <BodyM color="var(--c-yellow-400)">
                <Horizontal>
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <FormattedMessage {...glossary.insufficientBalance} />
                </Horizontal>
              </BodyM>
              <Button
                size="medium"
                color="secondary"
                to={
                  sport === Sport.BASEBALL
                    ? MLB_MARKET_COMMON_MARKET_DROP_CARD
                    : generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS_TYPE, {
                        params: {
                          type: Rarity.common,
                        },
                      })
                }
              >
                <FormattedMessage
                  id="commonPaymentBox.buyPoints"
                  defaultMessage="Drop card"
                />
              </Button>
            </Warning>
          )}
          {commonCardSummaryNode}
        </Grow>
      </Root>
    </Dialog>
  );
};
